import _ from 'lodash'
import * as React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import type { TenantUserListData } from 'api/tenants'
import { Role } from 'api/users'

import { selectSessionStatus } from 'slices/sessionSlice'
import { getTenantUserList, selectTenantsStatus } from 'slices/tenantsSlice'

import { Suggestion } from 'components/common'
import type { SuggestionItem } from 'components/common/types'

import styles from './WorkspaceAdminEdit.module.scss'

const UNREGISTERED_USER = '招待済み未登録ユーザー'

type Props = {
  admins: SuggestionItem[]
  members: SuggestionItem[]
  onChange: (admins: SuggestionItem[], members: SuggestionItem[]) => void
}

const WorkspaceAdminEdit: React.FC<Props> = props => {
  const { admins, members, onChange } = props

  const {
    user: { tenants },
  } = useSelector(selectSessionStatus, shallowEqual)
  const { users } = useSelector(selectTenantsStatus, shallowEqual)

  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch(getTenantUserList(tenants[0].tenantId))
  }, [dispatch, tenants])

  React.useEffect(() => {
    // Admin権限のユーザーを変更できない値として追加しておく
    const newAdmins: SuggestionItem[] = []
    users
      .filter(user => user.role === Role.Admin && user.name !== UNREGISTERED_USER)
      .forEach(adminUser => {
        newAdmins.push({ id: adminUser.id, value: adminUser.name })
      })
    if (!_.isEqual(admins, newAdmins)) {
      onChange(newAdmins, members)
    }
  }, [users, admins, members, onChange])
  const processAdminUsers = React.useMemo(
    () => users.filter(user => user.role === Role.ProcessAdmin && user.name !== UNREGISTERED_USER),
    [users]
  )

  const onSelectAdminUser = (item: SuggestionItem) => {
    const newMembers = [...members, { id: item.id.toString(), value: item.value }]
    onChange(admins, newMembers)
  }

  const removeAdminUser = (item: SuggestionItem) => {
    const newMembers = members.filter(member => member.id !== item.id.toString())
    onChange(admins, newMembers)
  }

  const remainingAdminUsers = React.useMemo(
    () =>
      processAdminUsers.reduce((acc: SuggestionItem[], cur: TenantUserListData) => {
        if (members.every(member => member.id !== cur.id) && cur.name) {
          acc.push({ id: cur.id, value: cur.name })
        }
        return acc
      }, []),
    [processAdminUsers, members]
  )

  return (
    <>
      <div className={styles.badgeContainer}>
        {admins.concat(members).map(member => {
          const isAdmin = users.some(u => u.id === member.id && u.role === Role.Admin)
          const color = isAdmin ? 'light-gray' : 'primary'
          return (
            <div
              key={member.id}
              className={`badge rounded-pill ${isAdmin && 'text-black'} bg-${color} fw-normal m-2 font-small`}
              style={{ maxWidth: '90%' }}
            >
              <div className="d-flex align-items-center">
                <div className="text-truncate">{member.value}</div>
                {!isAdmin && <i className="icf-close ms-1" role="button" onClick={() => removeAdminUser(member)} />}
              </div>
            </div>
          )
        })}
      </div>
      <div className="mt-3  text-muted">ワークスペース管理者を追加</div>
      <div className="d-flex">
        <Suggestion
          className="w-50"
          suggestionList={remainingAdminUsers}
          onSelect={onSelectAdminUser}
          placeholder="ユーザー名で検索"
        />
      </div>
    </>
  )
}

export default WorkspaceAdminEdit
